<script setup lang="ts">
import { usePhoneStore } from '@/stores/phone';
import { DATA_QA } from './phone-details.constants';
import ReadonlyField from '@/app/components/ReadonlyField/ReadonlyField.vue';
import { format } from 'date-fns';
import { useTargetStore } from '@/stores';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';

type Props = {
  number: string | null;
};

const props = defineProps<Props>();

const phoneStore = usePhoneStore();

const phone = phoneStore.watch(() => props.number, 'PhoneDetails');

const targetStore = useTargetStore();

const lastAuthor = targetStore.watch(
  () => phone.value.item?.lastAuthorId ?? null,
  'PhoneDetails',
);

const emit = defineEmits(['openChangelog']);
</script>

<template>
  <div :data-qa="DATA_QA.ROOT" class="d-d-flex d-fd-column d-w100p">
    <ReadonlyField
      :label="$t('PHONE_DETAILS_PHONE_NUMBER')"
      :loading="!phone.item"
      :value="phone.item?.formattedPhone"
      :data-meta="DATA_QA.PHONE_NUMBER"
      :skeleton-width="120"
      divider
    />
    <ReadonlyField
      :label="$t('PHONE_DETAILS_DNC')"
      :loading="!phone.item"
      :value="
        $t(
          phone.item?.dnc
            ? 'PHONE_DETAILS_BOOLEAN_TRUE'
            : 'PHONE_DETAILS_BOOLEAN_FALSE',
        )
      "
      :data-meta="DATA_QA.DNC"
      :skeleton-width="50"
    />
    <ReadonlyField
      :label="$t('PHONE_DETAILS_DNC_OPT_OUT_DATE')"
      :loading="!phone.item"
      :value="
        phone.item?.dateDncOptOut &&
        format(phone.item.dateDncOptOut, $t('PHONE_DETAILS_DATE_FORMAT'))
      "
      :data-meta="DATA_QA.DNC_OPT_OUT_DATE"
      :skeleton-width="140"
    />
    <ReadonlyField
      :label="$t('PHONE_DETAILS_DNC_EXPIRATION')"
      :loading="!phone.item"
      :value="
        phone.item?.dateDncExpires &&
        format(phone.item.dateDncExpires, $t('PHONE_DETAILS_DATE_FORMAT'))
      "
      :data-meta="DATA_QA.DNC_EXPIRATION"
      :skeleton-width="140"
    />
    <ReadonlyField
      :label="$t('PHONE_DETAILS_DNC_SCOPE')"
      :loading="!phone.item"
      :value="
        phone.item?.dncScope
          ? $ta('PHONE_DETAILS_DNT_SCOPE')[phone.item.dncScope]
          : '-'
      "
      :data-meta="DATA_QA.DNC_SCOPE"
      :skeleton-width="140"
      divider
    />
    <ReadonlyField
      :label="$t('PHONE_DETAILS_DNT')"
      :loading="!phone.item"
      :value="
        $t(
          phone.item?.dnt
            ? 'PHONE_DETAILS_BOOLEAN_TRUE'
            : 'PHONE_DETAILS_BOOLEAN_FALSE',
        )
      "
      :data-meta="DATA_QA.DNT"
      :skeleton-width="50"
    />
    <ReadonlyField
      :label="$t('PHONE_DETAILS_DNT_OPT_OUT_DATE')"
      :loading="!phone.item"
      :value="
        phone.item?.dateDntOptOut &&
        format(phone.item.dateDntOptOut, $t('PHONE_DETAILS_DATE_FORMAT'))
      "
      :data-meta="DATA_QA.DNT_OPT_OUT_DATE"
      :skeleton-width="140"
    />
    <ReadonlyField
      :label="$t('PHONE_DETAILS_DNT_EXPIRATION')"
      :loading="!phone.item"
      :value="
        phone.item?.dateDntExpires &&
        format(phone.item.dateDntExpires, $t('PHONE_DETAILS_DATE_FORMAT'))
      "
      :data-meta="DATA_QA.DNT_EXPIRATION"
      :skeleton-width="140"
    />
    <ReadonlyField
      :label="$t('PHONE_DETAILS_DNT_SCOPE')"
      :loading="!phone.item"
      :value="
        phone.item?.dntScope
          ? $ta('PHONE_DETAILS_DNT_SCOPE')[phone.item.dntScope]
          : '-'
      "
      :data-meta="DATA_QA.DNT_SCOPE"
      :skeleton-width="140"
      divider
    />
    <ReadonlyField
      :label="$t('PHONE_DETAILS_LAST_UPDATED')"
      :loading="!phone.item"
      :value="
        phone.item?.dateModified &&
        $t('PHONE_DETAILS_UPDATED_VALUE', {
          date: format(
            phone.item.dateModified,
            $t('PHONE_DETAILS_DATE_FORMAT'),
          ),
          name: lastAuthor.item?.name ?? 'Dialpad',
        })
      "
      :data-meta="DATA_QA.LAST_UPDATED"
      :skeleton-width="140"
    />
    <div class="d-mtn16 d-ml4">
      <DtButton
        importance="clear"
        class="d-fs-100"
        @click="emit('openChangelog')"
      >
        {{ $t('PHONE_DETAILS_VIEW_CHANGELOG') }}
      </DtButton>
    </div>
  </div>
</template>
